import type { Dispatch, FormEvent, SetStateAction } from 'react'
import React, { useCallback, useEffect, useState, useMemo } from 'react'

import { Box, Grid } from '@mui/material'
import { useAuthContext } from 'AuthProvider'
import { ClearButton, SearchButton, SearchCustomer, SelectCenter, SelectItem, SelectSupplier, SelectTypeCode, SearchDateRange } from 'components/commonparts'

import type { GetStockStockGetRequest, MstSupplier, MstCenter, ResponseResult } from 'api-client'
import type { MstCenterSeq } from 'hooks/useCenter'

type StockCenterSearchParam = GetStockStockGetRequest & { orgItemCodes?: string[] }

interface SearchConditionProps {
  searchParam: StockCenterSearchParam
  onSearch: (requestParam: StockCenterSearchParam) => void
  setSupplierInfo: Dispatch<SetStateAction<MstSupplier | undefined>>
  hasGridEdited?: boolean
  setOpen?: React.Dispatch<React.SetStateAction<boolean>>
  setDiscardChangeDialogOpen?: Dispatch<SetStateAction<boolean>>,
  searchTrigger?: boolean
  onCsvDownLoad?: () => void
  onExcelDownLoad?: () => void
  isCsvLoading?: boolean
  isXlsxLoading?: boolean
  result?: ResponseResult | undefined
}

function SearchCondition2({
  searchParam,
  onSearch,
  setSupplierInfo,
  hasGridEdited = false,
  setOpen,
  setDiscardChangeDialogOpen,
  searchTrigger,
  onCsvDownLoad,
  onExcelDownLoad,
  isCsvLoading,
  isXlsxLoading,
  result,
}: SearchConditionProps) {
  const { loginUserInfo, typeCode: typeCodes } = useAuthContext();

  const [centerIsValid, setCenterIsValid] = useState(true)
  const [supplierIsValid, setSupplierIsValid] = useState(true)
  const [centerId, setCenterId] = useState<number | null | undefined>(
    searchParam.centerIds?.length ? searchParam.centerIds[0] : searchParam.centerId
  )
  const [centerIdList, setCenterIdList] = useState<number[]>(searchParam.centerIds ?? [])
  const [centerInfo, setCenterInfo] = useState<MstCenter | undefined>(undefined)
  const [centerInfoList, setCenterInfoList] = useState<MstCenterSeq[]>([])
  const [customerCompanyId, setCustomerCompanyId] = useState<number | undefined>(
    // companyIdがログインユーザーの企業IDの場合はundefinedにする
    searchParam.companyId === loginUserInfo!.companyId ? undefined : searchParam.companyId
  )
  const [orgSupplierIdList, setOrgSupplierIdList] = useState<string[]>(searchParam.orgSupplierIds ?? [])
  const [itemCodeList, setItemCodeList] = useState<string[]>(searchParam.itemCodes ?? [])
  const [orgItemCodeList, setOrgItemCodeList] = useState<string[]>(searchParam.orgItemCodes ?? [])
  const [itemIsValid, setItemIsValid] = useState(true)
  const [viweUnitTypeCode, setViewUnitTypeCode] = useState(searchParam.viewUnitType)

  // At least one of Center/Supplier/Item must be selected to enable search
  const hasAnySelection = centerIdList.length > 0 || orgSupplierIdList.length > 0 || itemCodeList.length > 0
  // const [optimizeRangeCode, setOptimizeRangeCode] = useState(searchParam.optimizeRange)

  // 表示期間 state
  const [searchDateRangeStartType, setSearchDateRangeStartType] = useState<number | null | undefined>(Number(searchParam.startDateTypeCode))
  const [searchDateRangeEndType, setSearchDateRangeEndType] = useState<number | null | undefined>(Number(searchParam.endDateTypeCode))
  const [searchDateRangeStartDate, setSearchDateRangeStartDate] = useState<Date | null | undefined>(null)
  const [searchDateRangeEndDate, setSearchDateRangeEndDate] = useState<Date | null | undefined>(null)
  const [searchWeekNum, setSearchWeekNum] = useState<number | null | undefined>(null)
  const [clearSeq, setClearSeq] = useState(0)
  const [itemCloseSignal, setItemCloseSignal] = useState(0)
  const supplierIdsOnPanelOpenRef = React.useRef<string | null>(null)

  // Sync form state from URL/localStorage (searchParam) so form reflects initSearchParam and search runs with correct params
  const prevSearchParamRef = React.useRef<string>('')
  useEffect(() => {
    const ids = searchParam.orgSupplierIds ?? []
    const codes = searchParam.itemCodes ?? []
    const orgCodes = searchParam.orgItemCodes ?? []
    const centerIds = searchParam.centerIds ?? []
    const key = JSON.stringify({ ids, codes, orgCodes, centerIds, companyId: searchParam.companyId, viewUnitType: searchParam.viewUnitType, start: searchParam.startDateTypeCode, end: searchParam.endDateTypeCode })
    if (key !== prevSearchParamRef.current) {
      prevSearchParamRef.current = key
      setOrgSupplierIdList(ids)
      setItemCodeList(codes)
      setOrgItemCodeList(orgCodes)
      setCenterIdList(centerIds)
      setCenterId(centerIds.length > 0 ? centerIds[0] : searchParam.centerId)
      if (searchParam.companyId != null) {
        setCustomerCompanyId(searchParam.companyId === loginUserInfo!.companyId ? undefined : searchParam.companyId)
      }
      if (searchParam.viewUnitType != null) setViewUnitTypeCode(searchParam.viewUnitType)
      if (searchParam.startDateTypeCode != null) setSearchDateRangeStartType(Number(searchParam.startDateTypeCode))
      if (searchParam.endDateTypeCode != null) setSearchDateRangeEndType(Number(searchParam.endDateTypeCode))
    }
  }, [
    searchParam.orgSupplierIds,
    searchParam.itemCodes,
    searchParam.orgItemCodes,
    searchParam.centerIds,
    searchParam.centerId,
    searchParam.companyId,
    searchParam.viewUnitType,
    searchParam.startDateTypeCode,
    searchParam.endDateTypeCode,
    loginUserInfo?.companyId,
  ])

  const supplierIdsKey = useCallback((ids: string[]) => [...ids].sort().join('\u0001'), [])

  const clearItemSelection = useCallback((options?: { closeDropdown?: boolean }) => {
    setItemCodeList([])
    setOrgItemCodeList([])
    if (options?.closeDropdown !== false) {
      setItemCloseSignal((n) => n + 1)
    }
  }, [])

  const handleSupplierDropdownOpen = useCallback((ids: string[]) => {
    supplierIdsOnPanelOpenRef.current = supplierIdsKey(ids)
  }, [supplierIdsKey])

  /** リスト内で追加・削除したあと、ドロップダウンを閉じたときだけ商品をクリア */
  const handleSupplierDropdownClose = useCallback((ids: string[]) => {
    const snapshot = supplierIdsOnPanelOpenRef.current
    supplierIdsOnPanelOpenRef.current = null
    if (snapshot == null) {
      return
    }
    if (snapshot !== supplierIdsKey(ids)) {
      // 次フレームでクリア: 商品クリックでリストを開いてから value を空にする
      queueMicrotask(() => clearItemSelection({ closeDropdown: false }))
    }
  }, [supplierIdsKey, clearItemSelection])

  /** チップの X — ドロップダウンを開いていなくても即クリア＋商品リストを閉じる */
  const handleSupplierChipRemoved = useCallback(() => {
    supplierIdsOnPanelOpenRef.current = null
    clearItemSelection()
  }, [clearItemSelection])

  const onClear = () => {
    setCustomerCompanyId(undefined)
    setCenterId(undefined)
    setCenterIdList([])
    setCenterInfoList([])
    setOrgSupplierIdList([])
    setItemCodeList([])
    setOrgItemCodeList([])
    setClearSeq((prev) => prev + 1)
  }

  const handleSetOrgSupplierIdFromSelect = useCallback((v: React.SetStateAction<string | null | undefined>) => {
    const value = typeof v === 'function' ? undefined : v
    setOrgSupplierIdList((prev) => (value != null ? [value, ...prev.slice(1)] : prev.slice(1)))
  }, [])

  const handleSetItemCodeFromSelect = useCallback((v: React.SetStateAction<string | null | undefined>) => {
    const value = typeof v === 'function' ? undefined : v
    setItemCodeList(prev => (value != null ? [value, ...prev.slice(1)] : prev.slice(1)))
  }, [])

  const initialDateRangeValues = useMemo<{ start: string | undefined, end: string | undefined }>(() => {
    if (!typeCodes || typeCodes.length === 0) {
      return { start: undefined, end: undefined };
    }
    const SearchDateRangeStartShortGroup = typeCodes?.find((type) => type.typeGroup === 'search_date_range_start_short')
    const SearchDateRangeEndGroup = typeCodes?.find((type) => type.typeGroup === 'search_date_range_end')
    const defaultStartCode = SearchDateRangeStartShortGroup?.codes.find((type) => type.defaultFlg)?.typeCode
    const defaultEndCode = SearchDateRangeEndGroup?.codes.find((type) => type.defaultFlg)?.typeCode

    return { start: defaultStartCode, end: defaultEndCode };
  }, [typeCodes]);

  // Search using formEvent
  const handleFormSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    if (hasGridEdited) {
      if(setDiscardChangeDialogOpen) setDiscardChangeDialogOpen((prev) => !prev)
      return
    }
    if (hasAnySelection) {
      onSearch({
        companyId: customerCompanyId ?? loginUserInfo!.companyId,
        centerId: centerIdList.length === 1 ? centerIdList[0] : centerId,
        centerIds: centerIdList.length > 0 ? centerIdList : undefined,
        orgSupplierIds: orgSupplierIdList.length > 0 ? orgSupplierIdList : undefined,
        itemCodes: itemCodeList.length > 0 ? itemCodeList : undefined,
        orgItemCodes: orgItemCodeList.length > 0 ? orgItemCodeList : undefined,
        // optimizeRange: optimizeRangeCode,
        viewUnitType: viweUnitTypeCode,
        startDate: searchDateRangeStartDate ?? undefined,
        endDate: searchDateRangeEndDate ?? undefined,
        startDateTypeCode: searchDateRangeStartType ? String(searchDateRangeStartType) : initialDateRangeValues.start,
        endDateTypeCode: searchDateRangeEndType ? String(searchDateRangeEndType) : initialDateRangeValues.end,
      })
    }
  }

  const isFirstSearchTriggerEffect = React.useRef(true)
  useEffect(() => {
    // 初回マウントでは searchTrigger が false のまま effect が走り、親 StockCenter の初期検索と二重に onSearch になる。
    // 親が searchTrigger をトグルしたときだけ検索する（他画面の SearchCondition と同パターン）。
    if (isFirstSearchTriggerEffect.current) {
      isFirstSearchTriggerEffect.current = false
      return
    }
    // searchTriggerの変更を検知して、検索イベントを起こす
    // 親コンポーネントなどで検索を行いたい場合は、このトリガーを使用する
    if (hasAnySelection) {
      onSearch({
        companyId: customerCompanyId ?? loginUserInfo!.companyId,
        centerId: centerIdList.length === 1 ? centerIdList[0] : centerId,
        centerIds: centerIdList.length > 0 ? centerIdList : undefined,
        orgSupplierIds: orgSupplierIdList.length > 0 ? orgSupplierIdList : undefined,
        itemCodes: itemCodeList.length > 0 ? itemCodeList : undefined,
        orgItemCodes: orgItemCodeList.length > 0 ? orgItemCodeList : undefined,
        // optimizeRange: optimizeRangeCode,
        viewUnitType: viweUnitTypeCode,
        startDate: searchDateRangeStartDate ?? undefined,
        endDate: searchDateRangeEndDate ?? undefined,
        startDateTypeCode: searchDateRangeStartType ? String(searchDateRangeStartType) : initialDateRangeValues.start,
        endDateTypeCode: searchDateRangeEndType ? String(searchDateRangeEndType) : initialDateRangeValues.end,
      })
    }
  }, [searchTrigger])

  return (
    <Box component="form" onSubmit={handleFormSubmit} noValidate>
      <Grid container sx={{ mb: '0px' }}>
        <SearchCustomer
          title="会社"
          trCompanyId={loginUserInfo!.companyId}
          customerCompanyId={customerCompanyId}
          setCustomerCompanyId={setCustomerCompanyId}
          setCustomerCompanyInfo={() => {}}
          isRequred={false}
          setIsValid={() => {}}
        />
        <Grid sx={{ mr: 1 }}>
          <SelectCenter
            key={`center-${clearSeq}`}
            trCompanyId={customerCompanyId}
            centerId={centerId}
            centerIds={centerIdList}
            setCenterId={setCenterId}
            setCenterInfo={setCenterInfo}
            isRequired={false}
            setIsValid={setCenterIsValid}
            isMultiple
            setCenterIdList={setCenterIdList}
            setCenterInfoList={setCenterInfoList}
          />
        </Grid>
        <SelectSupplier
          key={`supplier-${clearSeq}`}
          trCompanyId={customerCompanyId}
          orgSupplierId={orgSupplierIdList[0]}
          orgSupplierIdList={orgSupplierIdList}
          setOrgSupplierId={handleSetOrgSupplierIdFromSelect}
          centerId={centerIdList.length === 1 ? centerIdList[0] : centerId}
          centerIdList={centerIdList.length > 1 ? centerIdList : undefined}
          setSupplierInfo={setSupplierInfo}
          isRequred={false}
          setIsValid={setSupplierIsValid}
          enabled
          alignmentFlag={(centerIdList.length > 0 || !!centerId) && orgSupplierIdList.length > 0}
          hideInfoMark
          isMultiple
          setOrgSupplierIdList={setOrgSupplierIdList}
          onMultipleDropdownOpen={handleSupplierDropdownOpen}
          onMultipleDropdownClose={handleSupplierDropdownClose}
          onMultipleChipRemoved={handleSupplierChipRemoved}
        />
      </Grid>
      <Grid container sx={{ mb: '0px', alignItems: 'flex-end' }}>
        <Grid sx={{ mr: 1 }}>
          <SelectItem
            key={`item-${clearSeq}`}
            trCompanyId={customerCompanyId ?? loginUserInfo!.companyId}
            setItemInfo={() => {}}
            isRequred={false}
            setIsValid={setItemIsValid}
            enabled
            isMultiple
            setItemCodeList={setItemCodeList}
            setOrgItemCodeList={setOrgItemCodeList}
            itemCodeList={itemCodeList}
            orgItemCodeList={orgItemCodeList}
            orgSupplierIdList={orgSupplierIdList.length > 0 ? orgSupplierIdList : undefined}
            closeDropdownSignal={itemCloseSignal}
          />
        </Grid>
        {/* <SelectTypeCode
          typeGroup="OptimizeRange"
          typeCode={optimizeRangeCode}
          setTypeCode={setOptimizeRangeCode}
          setTypeCodeInfo={() => {}}
        /> */}
        <SelectTypeCode
          typeGroup="ViewUnitType"
          typeCode={viweUnitTypeCode}
          setTypeCode={setViewUnitTypeCode}
          setTypeCodeInfo={() => {}}
        />
        <SearchDateRange
          // I（変更不可）初期値は未指定でOK（各グループの先頭を既定に）
          SearchDateRangeStart={ searchDateRangeStartType ? String(searchDateRangeStartType) : initialDateRangeValues.start}
          SearchDateRangeEnd={ searchDateRangeEndType ? String(searchDateRangeEndType) : initialDateRangeValues.end}
          isShortRange
          setSearchDateRangeStartType={setSearchDateRangeStartType}
          setSearchDateRangeEndType={setSearchDateRangeEndType}
          setSearchDateRangeStartDate={setSearchDateRangeStartDate}
          setSearchDateRangeEndDate={setSearchDateRangeEndDate}
          setSearchWeekNum={setSearchWeekNum}
        />
        <Box         
          sx={{ 
            display: "flex", 
            justifyContent: "center", 
            alignItems: "flex-end", 
            mx: 1,
            pb: 0.5,
            cursor: "pointer",
          }}
          >
           <SearchButton searchId="page" disabled={!hasAnySelection}/>
           <ClearButton onClick={onClear} />
        </Box>
      </Grid>
    </Box>
  )
}

export default SearchCondition2
